import {useRef,useState} from 'react';
import type {Device,PickupApplication,PickupEligibility} from '@pocketbuy/outdoor-business-types';
import {Button,Card} from '@pocketbuy/outdoor-design-system';
import {pickupApi} from '../../api';
import {ImeiCameraScanner} from '../../components/ImeiCameraScanner';
import {isValidImei} from '../../components/imei';
import {overduePickupFailure} from './usePickupEligibility';

type Scan={imei:string;device:Device};
type Props={pickup:PickupApplication;busy:boolean;blocked?:boolean;error:string;onSubmit:(imeis:string[])=>Promise<void>;onScansChange?:()=>void;onPickupBlocked?:(eligibility:PickupEligibility)=>void};

export function PickupScanForm({pickup,busy,blocked=false,error,onSubmit,onScansChange,onPickupBlocked}:Props){
 const [scans,setScans]=useState<Scan[]>([]);
 const [entry,setEntry]=useState('');
 const [scanError,setScanError]=useState('');
 const [checking,setChecking]=useState(false);
 const adding=useRef(false);
 const needed=pickup.items.reduce((total,item)=>total+item.quantity,0);
 const count=(skuId:string)=>scans.filter(scan=>scan.device.skuId===skuId).length;
 const complete=needed>0&&pickup.items.every(item=>count(item.skuId)===item.quantity)&&scans.length===needed;
 const add=async()=>{
  if(adding.current||busy||blocked||!isValidImei(entry))return;
  adding.current=true;setChecking(true);setScanError('');onScansChange?.();
  const imei=entry;
  try{
   const device=await pickupApi.validateDevice(pickup.id,imei,scans.map(scan=>scan.imei));
   if(scans.some(scan=>scan.device.id===device.id))throw new Error(`${device.skuName} (IMEI ${imei}) is already scanned. Scan only one IMEI per phone.`);
   setScans(current=>[...current,{imei,device}]);setEntry('');
  }catch(e){const restriction=overduePickupFailure(e);if(restriction&&onPickupBlocked){onPickupBlocked(restriction);setScanError('');}else setScanError(e instanceof Error?e.message:'Could not check this phone. Your accepted scans are kept. Try again.');}
  finally{adding.current=false;setChecking(false);}
 };
 return <>
  <Card className="pb-stack op-scan-card">
   <div className="op-scan-heading"><h2 className="op-section-title">Scan phones for pickup</h2><p className="pb-muted" role="status">{scans.length} of {needed} phones scanned</p></div>
   <div className="op-progress"><i style={{width:`${needed?scans.length/needed*100:0}%`}}/></div>
   <div className="op-list">{pickup.items.map(item=><div className="op-list-item" key={item.skuId}><span className="op-list-copy"><strong>{item.skuName}</strong><small>{count(item.skuId)} of {item.quantity} scanned</small></span>{count(item.skuId)===item.quantity&&<span aria-label="Model complete">✓</span>}</div>)}</div>
  </Card>
  {!complete&&<Card className="pb-stack op-scan-card">
   <p className="pb-muted">Scan one IMEI on each phone. Check the model before adding it.</p>
   <fieldset disabled={checking||busy||blocked} className="op-scan-controls pb-stack">
    <ImeiCameraScanner label="Next phone" value={entry} onChange={value=>{setEntry(value);setScanError('');onScansChange?.()}}/>
    {isValidImei(entry)&&<Button className="pb-full" disabled={checking||busy||blocked} onClick={()=>void add()}>{checking?'Checking phone…':'Add IMEI'}</Button>}
   </fieldset>
   {scanError&&<p className="op-error" role="alert">{scanError}</p>}
  </Card>}
  {scans.length>0&&<Card className="pb-stack op-scan-card"><h2 className="op-section-title">Scanned phones</h2><div className="op-list">{scans.map((scan,index)=><div className="op-list-item" key={scan.device.id}>
   <span className="op-list-icon">{index+1}</span><span className="op-list-copy"><strong>{scan.device.skuName}</strong><small>IMEI {scan.imei}</small></span>
   <button type="button" className="op-link" disabled={checking||busy} aria-label={`Remove ${scan.device.skuName}, IMEI ${scan.imei}`} onClick={()=>{setScans(scans.filter(item=>item.device.id!==scan.device.id));setScanError('');onScansChange?.()}}>Remove</button>
  </div>)}</div></Card>}
  {error&&<p className="op-error" role="alert">{error}</p>}
  {complete&&<div className="op-task-footer"><Button className="pb-full" disabled={busy||checking||blocked} onClick={()=>void onSubmit(scans.map(scan=>scan.imei))}>{busy?'Submitting…':'Submit Review'}</Button></div>}
 </>;
}
